import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [reviews, setReviews] = useState([]);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    // Fetch CMS content for homepage metadata and testimonials
    fetch('https://api.dev.auxo.dk/public/v2/content/pages?slug.da=index')
      ? fetch('https://api.dev.auxo.dk/public/v2/content/pages?slug.da=index')
          .then((res) => res.json())
          .then((data) => {
            if (data?.items && data.items.length > 0) {
              setPageData(data.items[0]);
            }
          })
          .catch(() => {})
      : null;

    fetch('https://api.dev.auxo.dk/public/v2/content/testimonials')
      .then((res) => res.json())
      .then((data) => {
        if (data?.items) {
          setReviews(data.items);
        }
      })
      .catch(() => {});
  }, []);

  const metaTitle = pageData?.meta_title || 'Rolskov Bowtech';
  const metaDesc =
    pageData?.meta_description ||
    'Personlig Bowtech-behandling hos Jørn Rolskov i Skårup på Fyn. Læs om Bowtech, behandlingen og de praktiske forhold, eller kontakt klinikken.';

  return (
    <>
      {/* Dynamic SEO Meta */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <link rel="canonical" href="https://rolskov-bowtech.dk/" />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#faf8f5] pt-8 pb-16 lg:pt-12 lg:pb-24 border-b border-[#228A47]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Left Column: Headline & Intro */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#e8f5ed] text-[#228A47] text-xs font-semibold tracking-wide">
                <i className="fa-regular fa-leaf" aria-hidden="true"></i>
                <span>Personlig Bowtech-behandling i Skårup på Fyn</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#062364] leading-[1.15]">
                En blid vej til mere ro og balance i kroppen
              </h1>

              <p className="text-base sm:text-lg text-[#132038] leading-relaxed max-w-2xl font-normal">
                Personlig Bowtech-behandling hos Jørn Rolskov i rolige og trygge rammer i Skårup på Fyn. Opnå behagelig afspænding og støt kroppens egne evner til at genoprette balancen.
              </p>

              {/* CTAs */}
              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-semibold text-white bg-[#228A47] hover:bg-[#1b713a] active:bg-[#155a2d] rounded-xl shadow-sm transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#228A47]"
                >
                  <span>Bestil tid</span>
                  <i className="fa-regular fa-calendar-check" aria-hidden="true"></i>
                </Link>

                <a
                  href="tel:27117557"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3.5 text-sm font-semibold text-[#062364] bg-[#f4efe6] hover:bg-[#e8f5ed] hover:text-[#228A47] rounded-xl transition-all duration-200 border border-[#228A47]/10"
                >
                  <i className="fa-regular fa-phone text-[#228A47]" aria-hidden="true"></i>
                  <span>Ring på 27 11 75 57</span>
                </a>

                <Link
                  to="/projects"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#228A47] hover:text-[#062364] transition-colors px-2 py-1"
                >
                  <span>Læs om Bowtech</span>
                  <i className="fa-regular fa-arrow-right" aria-hidden="true"></i>
                </Link>
              </div>

              {/* Quick Highlights */}
              <div className="pt-6 border-t border-[#228A47]/10 grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs font-medium text-[#062364]">
                <div className="flex items-center gap-2">
                  <i className="fa-regular fa-circle-check text-[#228A47]" aria-hidden="true"></i>
                  <span>Varer ca. 1 time</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-regular fa-circle-check text-[#228A47]" aria-hidden="true"></i>
                  <span>Kan udføres uden på tøjet</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-regular fa-circle-check text-[#228A47]" aria-hidden="true"></i>
                  <span>400 kr. pr. behandling</span>
                </div>
              </div>
            </div>

            {/* Right Column: Hero Image */}
            <div className="lg:col-span-5 relative flex">
              <div className="relative rounded-2xl overflow-hidden shadow-lg border border-[#228A47]/15 bg-white w-full h-full flex flex-col justify-between">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/0424b756-a2fd-4ec3-9a16-f52eef89d933/imgi-14-8408-edit.jpg"
                  alt="Jørn Rolskov i rolig samtale i klinikken i Skårup"
                  className="w-full h-full min-h-[380px] sm:min-h-[460px] object-cover"
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#062364]/85 via-[#062364]/35 to-transparent p-6 text-white">
                  <p className="text-xs font-semibold text-[#228A47] uppercase tracking-wider bg-white/90 backdrop-blur-xs inline-block px-2.5 py-0.5 rounded-md mb-1">
                    Jørn Rolskov
                  </p>
                  <p className="text-sm font-medium text-slate-100">
                    Certificeret Bowtechbehandler &amp; uddannet lærer
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Personal Welcome Section */}
      <section className="py-16 sm:py-24 bg-[#faf8f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#f4efe6]/70 rounded-3xl overflow-hidden border border-[#228A47]/15 shadow-sm grid grid-cols-1 lg:grid-cols-2">
            
            {/* Left Column: Full-bleed image */}
            <div className="relative min-h-[340px] sm:min-h-[420px] lg:min-h-full w-full overflow-hidden">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/0424b756-a2fd-4ec3-9a16-f52eef89d933/8380.jpg"
                alt="Jørn Rolskov - Bowtech-behandler"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* Right Column: Vertically centered content block */}
            <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center space-y-6">
              <div className="space-y-2">
                <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-[#228A47]">
                  Personlig velkomst
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#062364] tracking-tight leading-tight">
                  Velkommen hos Rolskov Bowtech
                </h2>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-[#062364]/85 leading-relaxed font-normal">
                <p>
                  Jeg er uddannet lærer med idræt som linjefag. Har i mange år arbejdet med gymnastik og idræt, hvor skader og behandling af disse har haft min interesse.
                </p>
                <p>
                  Blev uddannet Bowtechbehandler i november 2014, og har siden praktiseret behandlingen sideløbende med mit lærerjob i rolige og trygge rammer i Skårup.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/om-jorn"
                  className="rounded-xl bg-[#228A47] hover:bg-[#1b6e38] text-white px-7 py-3.5 text-sm sm:text-base font-semibold transition-all shadow-sm inline-flex items-center gap-2 group"
                >
                  <span>Lær mig bedre at kende</span>
                  <i className="fa-regular fa-arrow-right group-hover:translate-x-0.5 transition-transform" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/behandlingen"
                  className="rounded-xl border-2 border-[#228A47] text-[#062364] hover:bg-[#228A47] hover:text-white px-7 py-3.5 text-sm sm:text-base font-semibold transition-all inline-flex items-center gap-2 group"
                >
                  <span>Om behandlingen</span>
                  <i className="fa-regular fa-arrow-right group-hover:translate-x-0.5 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* What is Bowtech? Section */}
      <section className="py-16 bg-[#faf8f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#228A47] bg-[#e8f5ed] px-3 py-1 rounded-full">
              Holistisk muskel- og bindevævsterapi
            </span>
            <h2 className="text-3xl font-bold text-[#062364]">
              Hvad er BOWTECH?
            </h2>
            <p className="text-base text-[#132038]">
              BOWTECH er en muskel- og bindevævsterapi, der bringer kroppen i ro og stimulerer kroppens evne til at helbrede sig selv.
            </p>
          </div>

          {/* Core Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            
            <div className="space-y-5 bg-white p-8 rounded-2xl border border-[#228A47]/10 shadow-xs">
              <h3 className="text-xl font-bold text-[#062364]">
                Balance i kroppen
              </h3>
              <p className="text-sm text-[#132038] leading-relaxed">
                BOWTECH har i mere end 50 år med stor succes været anvendt til behandling af mange forskellige helbredsproblemer, både akutte- og kroniske skader.
              </p>
              <p className="text-sm text-[#132038] leading-relaxed">
                Efter ganske få behandlinger opnår de fleste en behagelig afspænding i muskler og led og oplever en vedvarende sænkning af hverdagens generelle stressniveau.
              </p>
              <p className="text-sm leading-relaxed font-semibold text-[#228A47]">
                Ofte forsvinder akutte smerter helt efter en til tre behandlinger. Mere kroniske tilstande behandles over længere tid og kræver opfølgning.
              </p>

              <div className="pt-2">
                <Link
                  to="/projects"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#228A47] hover:text-[#062364] transition-colors"
                >
                  <span>Læs mere om Bowtech</span>
                  <i className="fa-regular fa-arrow-right" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* List of Symptoms Bowtech helps with */}
            <div className="bg-[#e8f5ed] p-8 rounded-2xl border border-[#228A47]/15 space-y-4">
              <h3 className="text-lg font-bold text-[#062364]">
                BOWTECH behandling har gode og vedvarende resultater ved f.eks.:
              </h3>
              
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm text-[#062364] font-medium">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#228A47] shrink-0" aria-hidden="true"></i>
                  <span>Sportsskader</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#228A47] shrink-0" aria-hidden="true"></i>
                  <span>Nakke-/skulderproblemer (f.eks. ”frossen skulder”)</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#228A47] shrink-0" aria-hidden="true"></i>
                  <span>Knæ- / ankelproblemer</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#228A47] shrink-0" aria-hidden="true"></i>
                  <span>Ryg - / iskiassmerter</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#228A47] shrink-0" aria-hidden="true"></i>
                  <span>Migræne og hovedpine</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#228A47] shrink-0" aria-hidden="true"></i>
                  <span>Betændelsestilstande</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#228A47] shrink-0" aria-hidden="true"></i>
                  <span>Stress og spændinger</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#228A47] shrink-0" aria-hidden="true"></i>
                  <span>Skævheder i bevægeapparatet</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#228A47] shrink-0" aria-hidden="true"></i>
                  <span>Allergi</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#228A47] shrink-0" aria-hidden="true"></i>
                  <span>Fordøjelsesproblemer</span>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* How Treatment Works Sequence */}
      <section className="py-16 bg-[#f4efe6]/40 border-y border-[#228A47]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-semibold text-[#228A47] uppercase tracking-wider">
              Behandlingsforløbet
            </span>
            <h2 className="text-3xl font-bold text-[#062364]">
              Sådan foregår en behandling
            </h2>
            <p className="text-sm text-[#132038]">
              Behandlingen varer ca. 1 time og er tilpasset din krops behov i et roligt tempo.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Step 1 */}
            <div className="bg-white p-6 rounded-2xl border border-[#228A47]/10 shadow-xs space-y-3 relative">
              <div className="w-10 h-10 rounded-xl bg-[#e8f5ed] text-[#228A47] flex items-center justify-center text-lg font-bold">
                <i className="fa-regular fa-comments" aria-hidden="true"></i>
              </div>
              <span className="text-xs font-bold text-[#228A47] uppercase tracking-wider">Trin 1</span>
              <h3 className="text-lg font-bold text-[#062364]">Indledende samtale</h3>
              <p className="text-xs text-[#132038] leading-relaxed">
                Vi starter med en kort samtale om dine symptomer, skader og nuværende tilstand.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white p-6 rounded-2xl border border-[#228A47]/10 shadow-xs space-y-3 relative">
              <div className="w-10 h-10 rounded-xl bg-[#e8f5ed] text-[#228A47] flex items-center justify-center text-lg font-bold">
                <i className="fa-regular fa-hand-holding-heart" aria-hidden="true"></i>
              </div>
              <span className="text-xs font-bold text-[#228A47] uppercase tracking-wider">Trin 2</span>
              <h3 className="text-lg font-bold text-[#062364]">Blide, målrettede greb</h3>
              <p className="text-xs text-[#132038] leading-relaxed">
                Behandlingen består af præcise små bevægelser over muskler, sener, ledbånd og nerver.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white p-6 rounded-2xl border border-[#228A47]/10 shadow-xs space-y-3 relative">
              <div className="w-10 h-10 rounded-xl bg-[#e8f5ed] text-[#228A47] flex items-center justify-center text-lg font-bold">
                <i className="fa-regular fa-pause" aria-hidden="true"></i>
              </div>
              <span className="text-xs font-bold text-[#228A47] uppercase tracking-wider">Trin 3</span>
              <h3 className="text-lg font-bold text-[#062364]">Korte pauser (2 min.)</h3>
              <p className="text-xs text-[#132038] leading-relaxed">
                Mellem hver serie af greb holdes korte pauser, så nervesystemet kan registrere stimulansen.
              </p>
            </div>

            {/* Step 4 */}
            <div className="bg-white p-6 rounded-2xl border border-[#228A47]/10 shadow-xs space-y-3 relative">
              <div className="w-10 h-10 rounded-xl bg-[#e8f5ed] text-[#228A47] flex items-center justify-center text-lg font-bold">
                <i className="fa-regular fa-sparkles" aria-hidden="true"></i>
              </div>
              <span className="text-xs font-bold text-[#228A47] uppercase tracking-wider">Trin 4</span>
              <h3 className="text-lg font-bold text-[#062364]">Ro &amp; opfølgning</h3>
              <p className="text-xs text-[#132038] leading-relaxed">
                Efter behandlingen opleves behagelig afspænding. Opfølgning planlægges efter behov.
              </p>
            </div>

          </div>

          <div className="text-center pt-2">
            <Link
              to="/behandlingen"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-[#062364] bg-white hover:bg-[#e8f5ed] border border-[#228A47]/20 rounded-xl transition-colors"
            >
              <span>Læs den fulde beskrivelse af behandlingen</span>
              <i className="fa-regular fa-arrow-right" aria-hidden="true"></i>
            </Link>
          </div>

        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative py-24 sm:py-32 bg-[#062364] text-white overflow-hidden">
        {/* Prominent background image with dark overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/0424b756-a2fd-4ec3-9a16-f52eef89d933/fullsizerender-1.jpg"
            alt="Rolskov Bowtech klinik"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#062364]/50 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#062364]/75 via-[#062364]/40 to-[#062364]/65" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-8">
          <h2 className="text-3xl sm:text-5xl font-serif font-bold tracking-tight text-white leading-tight">
            Har du spørgsmål til Bowtech?
          </h2>

          <p className="text-lg sm:text-xl font-sans text-slate-100 max-w-2xl mx-auto leading-relaxed">
            Du er altid meget velkommen til at kontakte Jørn for en uforpligtende samtale om, hvorvidt Bowtech kan hjælpe dig.
          </p>

          <div className="pt-4 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <a
              href="tel:27117557"
              className="whitespace-nowrap inline-flex items-center gap-2 sm:gap-3 px-5 sm:px-8 py-2.5 sm:py-4 bg-white text-[#228A47] hover:bg-[#faf8f5] hover:text-[#1b713a] font-bold text-xs sm:text-sm tracking-wide uppercase transition-all duration-200 rounded-xl shadow-lg focus:outline-none focus:ring-2 focus:ring-white"
            >
              <i className="fa-solid fa-phone" aria-hidden="true"></i>
              <span>Ring på 27 11 75 57</span>
            </a>

            <a
              href="mailto:joern@rolskov-bowtech.dk"
              className="whitespace-nowrap inline-flex items-center gap-2 sm:gap-3 px-5 sm:px-8 py-2.5 sm:py-4 bg-[#228A47] text-white hover:bg-[#1b713a] font-bold text-xs sm:text-sm tracking-wide uppercase transition-all duration-200 rounded-xl shadow-lg focus:outline-none focus:ring-2 focus:ring-white"
            >
              <i className="fa-regular fa-envelope" aria-hidden="true"></i>
              <span>Skriv til joern@rolskov-bowtech.dk</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
